import React, { useState, useEffect, useRef } from "react";
import { useFormik, FieldArray, FormikProvider, Form } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import InputField from "../../../../component/input-field";
import CssTransition from "../../../../component/layout/css-transition";
import "../../../../css/theme.css";
import { useDispatch, useSelector } from "react-redux";
import { isFloat, sanitizeInput } from "../../../../utils";
import swal from "@sweetalert/with-react";
import CustomeModal from "../../../../component/modal-popup/customeModal";
import {
  getAllProductDataList,
  getInventoryAvailableProduct,
} from "../../../../redux/actions/productMaster";
import {
  getAllSkuData,
  getInventoryAvailableSKUs,
} from "../../../../redux/actions/skuMaster";
import { addCorrectionOrderData } from "../../../../redux/actions/correctionOrder";

function CreateAndUpdateCorrectionOrder({
  modalOpen,
  setModalOpen,
  update,
  setUpdate,
  updateCounter,
  setUpdateCounter,
}) {
  const modalContent = useRef();
  const typeOptions = [
    {
      label: "Product",
      value: "product",
    },
    {
      label: "SKU",
      value: "sku",
    },
  ];

  // State
  const [loader, setLoader] = useState(false);
  const [productOptions, setProductOptions] = useState([]);
  const [skuOptions, setSkuOptions] = useState([]);
  const [availableStocks, setAvailableStocks] = useState();

  // Redux
  const dispatch = useDispatch();
  const { allProductData } = useSelector((state) => state.productMaster);
  const { allSkuData } = useSelector((state) => state.skuMaster);

  // Function to get Product List
  const getProductList = () => {
    const request = {
      onError: (error) => {
        swal({
          content: (
            <CustomeModal
              type="error"
              title="Error!"
              content={error?.message}
            />
          ),
          buttons: false,
          closeOnClickOutside: false,
          closeOnEsc: false,
        });
      },
    };
    dispatch(getAllProductDataList(request));
  };

  // Function to GET SKU List
  const getSkuList = () => {
    const request = {
      onError: (error) => {
        swal({
          content: (
            <CustomeModal
              type="error"
              title="Error!"
              content={error?.message}
            />
          ),
          buttons: false,
          closeOnClickOutside: false,
          closeOnEsc: false,
        });
      },
    };
    dispatch(getAllSkuData(request));
  };

  // Function to GET Inventory SKUs Available Stock
  const getAvailableSkuStocks = (sku_master_id) => {
    const request = {
      sku_master_id,
      onSuccess: (result) => {
        setAvailableStocks(result.data);
      },
      onError: () => {},
    };
    dispatch(getInventoryAvailableSKUs(request));
  };

  // Function to GET Inventory Product Available Stock
  const getAvailableProductStock = (product_master_id) => {
    const request = {
      product_master_id,
      onSuccess: (result) => {
        setAvailableStocks(result.data);
      },
      onError: (err) => {},
    };
    dispatch(getInventoryAvailableProduct(request));
  };

  useEffect(() => {
    getProductList();
    getSkuList();
  }, []);

  useEffect(() => {
    if (allSkuData.length > 0) {
      setSkuOptions(
        allSkuData.map((item) => {
          const obj = {
            label: item.sku_name,
            value: item.sku_master_id,
          };
          return obj;
        })
      );
    }
    if (allProductData) {
      setProductOptions(
        allProductData.map((item) => {
          const obj = {
            label: item.product_name,
            value: item.product_master_id,
          };
          return obj;
        })
      );
    }
  }, [allSkuData, allProductData]);

  // Function to ADD Correction Order
  const addCorrectionOrder = () => {
    setLoader(true);
    const request = {
      payload: formik.values,
      onSuccess: () => {
        setLoader(false);
        setModalOpen(false);
        setUpdate(update + 1);
        setUpdateCounter(updateCounter + 1)
        formik.resetForm();
        setAvailableStocks();
      },
      onError: (err) => {
        setLoader(false);
      },
    };
    // console.log("payload ==>", request.payload);
    dispatch(addCorrectionOrderData(request));
  };

  const validationSchema = Yup.object().shape({
    type: Yup.string().required("This field is required"),
    remarks: Yup.string().nullable(),
    reference_no: Yup.string().nullable(),
    items: Yup.object().shape({
      id: Yup.string().required("This field is required"),
      qty: Yup.string().required("This field is required"),
    }),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      type: typeOptions[0].value,
      reference_no: "",
      remarks: "",
      items: {
        id: "",
        qty: "",
      },
    },
    validationSchema,
    onSubmit: () => {
      addCorrectionOrder();
    },
  });

  useEffect(() => {
    if (formik.values.items.id) {
      if (formik.values.type === "product") {
        getAvailableProductStock(formik.values.items.id);
      } else {
        getAvailableSkuStocks(formik.values.items.id);
      }
    } else {
      setAvailableStocks();
    }
  }, [formik.values.items.id]);

  const getDropDownValue = (value) => {
    if (formik.values.type === "product") {
      const dropDownVal = productOptions.find((item) => item?.value === value);
      return dropDownVal ? dropDownVal : "";
    } else {
      const dropDownVal = skuOptions.find((item) => item?.value === value);
      return dropDownVal ? dropDownVal : "";
    }
  };

  const getUnit = (id) => {
    if (formik.values.type === "product") {
      const product = allProductData.find(
        (item) => item.product_master_id === id
      );
      return product ? product?.master_uom?.master_uom_name : "";
    } else {
      const sku = allSkuData.find((item) => item.sku_master_id === id);
      return sku ? sku?.master_uom?.master_uom_name : "";
    }
  };

  return (
    <>
      <CssTransition
        className="fixed -inset-2 bg-slate-900 bg-opacity-30 z-50 transition-opacity"
        show={modalOpen}
        enter="transition ease-out duration-200"
        enterStart="opacity-0"
        enterEnd="opacity-100"
        leave="transition ease-out duration-100"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
        aria-hidden="true"
      />
      <CssTransition
        id="createPurchaseOrder"
        className="fixed inset-0 z-50 overflow-hidden flex items-center my-4 justify-center px-4 sm:px-6"
        role="dialog"
        aria-modal="true"
        show={modalOpen}
        enter="transition ease-in-out duration-200"
        enterStart="opacity-0 translate-y-4"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-in-out duration-200"
        leaveStart="opacity-100 translate-y-0"
        leaveEnd="opacity-0 translate-y-4"
      >
        <div
          ref={modalContent}
          className="bg-white w-full overflow-auto max-w-3xl max-h-full rounded-[20px] shadow-lg flex flex-col py-6 px-6 space-y-4"
        >
          <h1 className="text-center text-xl font-semibold text-[#1E293B] leading-[150%]">
            Correction Order
          </h1>

          <div className="bg-[--bg-color-50] rounded-md p-4 space-y-6">
            <h4 className="text-[#535860] font-bold leading-[100%] text-base">
              Basic Details
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="w-full">
                <InputField
                  label="Correction Order Number"
                  name="correction_order_no"
                  disabled
                  value="TBA"
                />
              </div>
              <div className="w-full">
                <label
                  className={`block text-sm font-medium mb-1 leading-3 tracking-tight`}
                  htmlFor="card-city"
                >
                  Type&nbsp;
                  <span className="text-rose-500">*</span>
                </label>
                <Select
                  className="basic-single text-sm unresetText"
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      backgroundColor: "white",
                      borderColor: "lightgray",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    placeholder: (provided, state) => ({
                      ...provided,
                      color: "#9297A2",
                      fontSize: "14px",
                    }),
                  }}
                  classNames={{
                    control: (style) => {
                      return `!border !shadow-sm ${
                        style.isFocused
                          ? "!border-[--bg-color-300]/50"
                          : "!border-slate-200 hover:!border-gray-300"
                      }`;
                    },
                    container: (style) => {
                      return "!h-[37.5px] !font-inter !text-sm";
                    },
                    menuList: (style) => {
                      return `!max-h-[200px]`;
                    },
                  }}
                  onChange={(type) => {
                    formik.setFieldValue("type", type?.value);
                  }}
                  name="type"
                  onBlur={formik.handleBlur}
                  value={
                    formik.values.type
                      ? typeOptions.find(
                          (element) => element.value === formik.values.type
                        )
                      : ""
                  }
                  options={typeOptions}
                  placeholder="Select Type"
                />
                {Boolean(formik.touched.type && formik.errors.type) && (
                  <div className="text-xs text-rose-600 pt-1 pl-1">
                    {formik.errors.type}
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="w-full">
                <InputField
                  label="Correction Order Ref. No."
                  name="reference_no"
                  onBlur={formik.handleBlur}
                  onChange={(e) => {
                    const value = sanitizeInput(e.target.value);
                    formik.setFieldValue("reference_no", value);
                  }}
                  value={formik.values.reference_no}
                  placeholder="Enter Correction Order Ref. No."
                />
              </div>
              <div className="w-full">
                <InputField
                  label="Remarks"
                  name="remarks"
                  onBlur={formik.handleBlur}
                  onChange={(e) => {
                    const value = sanitizeInput(e.target.value);
                    formik.setFieldValue("remarks", value);
                  }}
                  value={formik.values.remarks}
                  error={
                    Boolean(formik.touched.remarks && formik.errors.remarks)
                      ? formik.errors.remarks
                      : null
                  }
                  placeholder="Enter Remarks"
                />
              </div>
            </div>
          </div>

          <div className="bg-[--bg-color-50] rounded-md p-4 space-y-6 overflow-y-auto scrollbar scrollbar-w-2 scrollbar-thumb-rounded-full scrollbar-track-rounded-full scrollbar-thumb-gray-300 scrollbar-track-gray-100 h-full">
            <h4 className="text-[#535860] font-bold leading-[100%] text-base">
              Ordered SKUs / Products
            </h4>
            <div className="grid grid-cols-2 gap-2">
              <div className="w-full">
                <label
                  className={`block text-sm font-medium mb-1 leading-3 tracking-tight`}
                  htmlFor="card-city"
                >
                  {formik.values.type === "product" ? "Products" : "SKUs"}
                  &nbsp;
                  <span className="text-rose-500">*</span>
                </label>
                <Select
                  className="basic-single text-sm unresetText"
                  menuPortalTarget={document.body}
                  menuPlacement="top"
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      backgroundColor: "white",
                      borderColor: "lightgray",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    placeholder: (provided, state) => ({
                      ...provided,
                      color: "#9297A2",
                      fontSize: "14px",
                    }),
                  }}
                  classNames={{
                    control: (style) => {
                      return `!border !shadow-sm ${
                        style.isFocused
                          ? "!border-[--bg-color-300]/50"
                          : "!border-slate-200 hover:!border-gray-300"
                      }`;
                    },
                    container: (style) => {
                      return "!h-[37.5px] !font-inter !text-sm";
                    },
                    menuList: (style) => {
                      return `!max-h-[200px]`;
                    },
                  }}
                  onChange={(option) => {
                    formik.setFieldValue(`items.id`, option?.value);
                  }}
                  name="id"
                  onBlur={formik.handleBlur}
                  value={
                    formik.values.items.id
                      ? getDropDownValue(formik.values.items.id)
                      : ""
                  }
                  options={
                    formik.values.type === "product"
                      ? productOptions
                      : skuOptions
                  }
                  placeholder={`Select ${
                    formik.values.type === "product" ? "Product" : "SKU"
                  }`}
                />
                {Boolean(
                  formik?.touched?.items?.id && formik?.errors?.items?.id
                ) && (
                  <div className="text-xs text-rose-600 pt-1 pl-1">
                    {formik?.errors?.items?.id}
                  </div>
                )}
              </div>
              <div className="w-full grid grid-cols-3 gap-2">
                <div className="w-full">
                  <InputField
                    label="Sys. Quantity"
                    value={isFloat(availableStocks) ? availableStocks?.toFixed(2) : availableStocks}
                    disabled
                    suffix={getUnit(formik.values.items.id)}
                  />
                </div>
                <div className="w-full">
                  <InputField
                    label="Act. Quantity"
                    required
                    name="qty"
                    onBlur={formik.handleBlur}
                    onChange={(e) => {
                      const value = sanitizeInput(e.target.value);
                      formik.setFieldValue(`items.qty`, value);
                    }}
                    value={formik.values.items.qty}
                    error={
                      Boolean(
                        formik?.touched?.items?.qty &&
                          formik?.errors?.items?.qty
                      )
                        ? formik?.errors?.items?.qty
                        : null
                    }
                    suffix={getUnit(formik.values.items.id)}
                  />
                </div>
                <div className="w-full flex justify-start items-center">
                  {formik.values.items.id && formik.values.items.qty && (
                    <p
                      className={`text-sm font-semibold tracking-tight leading-[100%] ${
                        Number(formik.values.items.qty) >
                        Number(availableStocks)
                          ? "text-[#27AE60]"
                          : "text-rose-500"
                      }  mt-6`}
                    >
                      {Number(formik.values.items.qty) > Number(availableStocks)
                        ? `+ ${
                            Number(Number(formik.values.items.qty) - Number(availableStocks)).toFixed(2)
                          }`
                        : `${
                            Number(Number(formik.values.items.qty) - Number(availableStocks)).toFixed(2)
                          }`}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center">
            <button
              className="btn bg-white border-slate-200 hover:border-slate-300 text-slate-600 cursor-pointer"
              onClick={() => {
                setModalOpen(false);
                formik.resetForm();
                setAvailableStocks();
              }}
            >
              Cancel
            </button>

            <button
              className="btn bg-[--bg-color-500] hover:bg-[--bg-color-600] text-white cursor-pointer"
              onClick={() => {
                formik.handleSubmit();
              }}
            >
              {loader ? (
                <svg
                  className="animate-spin w-5 h-45 fill-current shrink-0"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
                </svg>
              ) : (
                "Create Correction Order"
              )}
            </button>
          </div>
        </div>
      </CssTransition>
    </>
  );
}

export default CreateAndUpdateCorrectionOrder;
